import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { API } from "@/App";
import { MapPin, Users, Trophy, ChevronDown } from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_crm-news-hub/artifacts/srv2lps9_ChatGPT%20Image%20Apr%202%2C%202026%2C%2010_47_10%20AM.png";
const DEFAULT_CENTER = [38.0293, -78.4767];
const DEFAULT_ZOOM = 12;

const makeMarkerIcon = (color = "#0C2E3C") => new L.DivIcon({
  className: "",
  html: `<div style="background:${color};width:30px;height:30px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.35);">
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
  </div>`,
  iconSize: [30, 30],
  iconAnchor: [15, 15],
});

// Auto-fit map bounds to markers
const FitBounds = ({ locations }) => {
  const map = useMap();
  useEffect(() => {
    if (locations.length > 0) {
      const bounds = L.latLngBounds(locations.map((l) => [l.lat, l.lng]));
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 14 });
    }
  }, [locations, map]);
  return null;
};

const TeamMapPage = () => {
  const [mapData, setMapData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMapData = async () => {
      try {
        const res = await axios.get(`${API}/map/public`);
        setMapData(res.data);
      } catch (err) { console.error("Failed to fetch map data:", err); }
      finally { setLoading(false); }
    };
    fetchMapData();
  }, []);

  const validLocations = (mapData?.locations || []).filter((l) => l.lat && l.lng);

  return (
    <div className="min-h-screen bg-[#F0F4F3]">
      <Helmet>
        <title>Our Community | Commonwealth FC Youth Soccer Charlottesville</title>
        <meta name="description" content="See where Commonwealth FC players come from across Charlottesville and Albemarle County. Find practice fields, game venues, and training locations." />
        <meta property="og:title" content="Our Community | Commonwealth FC" />
        <meta property="og:url" content="https://commonwealthfc.org/map" />
        <link rel="canonical" href="https://commonwealthfc.org/map" />
      </Helmet>

      <nav className="bg-[#0C2E3C] sticky top-0 z-[1000]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-14">
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Commonwealth FC" className="h-9 w-auto" />
            <span className="text-sm font-bold uppercase tracking-wider text-[#F0F4F3] hidden sm:block">Commonwealth FC</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-[#F0F4F3]/70 hover:text-[#C8E64E] text-sm font-medium">Home</Link>
            <Link to="/join" className="text-[#F0F4F3]/70 hover:text-[#C8E64E] text-sm font-medium">Join</Link>
            {/* Tryouts & Events dropdown */}
            <div className="relative group">
              <button className="text-[#F0F4F3]/70 hover:text-[#C8E64E] text-sm font-medium flex items-center gap-1">
                Tryouts & Events <ChevronDown size={13} />
              </button>
              <div className="absolute right-0 top-full pt-2 hidden group-hover:block z-50">
                <div className="bg-[#0C2E3C] border border-[#F0F4F3]/10 shadow-xl min-w-[170px] py-1">
                  <Link to="/tryouts" className="block px-4 py-2 text-sm text-[#F0F4F3]/80 hover:text-[#C8E64E] hover:bg-[#F0F4F3]/5">Tryouts</Link>
                  <Link to="/events" className="block px-4 py-2 text-sm text-[#F0F4F3]/80 hover:text-[#C8E64E] hover:bg-[#F0F4F3]/5">Events Calendar</Link>
                </div>
              </div>
            </div>
            {/* Club Info dropdown */}
            <div className="relative group">
              <button className="text-[#F0F4F3]/70 hover:text-[#C8E64E] text-sm font-medium flex items-center gap-1">
                Club Info <ChevronDown size={13} />
              </button>
              <div className="absolute right-0 top-full pt-2 hidden group-hover:block z-50">
                <div className="bg-[#0C2E3C] border border-[#F0F4F3]/10 shadow-xl min-w-[170px] py-1">
                  <Link to="/coaches" className="block px-4 py-2 text-sm text-[#F0F4F3]/80 hover:text-[#C8E64E] hover:bg-[#F0F4F3]/5">Bring Your Team</Link>
                  <Link to="/highlights" className="block px-4 py-2 text-sm text-[#F0F4F3]/80 hover:text-[#C8E64E] hover:bg-[#F0F4F3]/5">Player Highlights</Link>
                  <Link to="/sponsors" className="block px-4 py-2 text-sm text-[#F0F4F3]/80 hover:text-[#C8E64E] hover:bg-[#F0F4F3]/5">Our Sponsors</Link>
                  <Link to="/referral" className="block px-4 py-2 text-sm text-[#F0F4F3]/80 hover:text-[#C8E64E] hover:bg-[#F0F4F3]/5">Refer a Friend</Link>
                  <Link to="/livestream" className="block px-4 py-2 text-sm text-[#F0F4F3]/80 hover:text-[#C8E64E] hover:bg-[#F0F4F3]/5">Live Streams</Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="text-center mb-8">
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#0C2E3C] mb-2" data-testid="map-page-title">OUR COMMUNITY</h1>
          <p className="text-[#6B8A8F] text-base">Where our players train, play, and call home</p>
        </div>

        {mapData && (
          <div className="grid grid-cols-3 gap-4 mb-8 max-w-xl mx-auto" data-testid="map-stats">
            <div className="bg-white border border-[#0C2E3C]/10 p-4 text-center">
              <Users size={20} className="mx-auto text-[#C8E64E] mb-1" />
              <p className="text-2xl font-bold text-[#0C2E3C]">{mapData.total_players}</p>
              <p className="text-[10px] text-[#6B8A8F] uppercase font-bold tracking-wider">Players</p>
            </div>
            <div className="bg-white border border-[#0C2E3C]/10 p-4 text-center">
              <MapPin size={20} className="mx-auto text-[#C8E64E] mb-1" />
              <p className="text-2xl font-bold text-[#0C2E3C]">{validLocations.length}</p>
              <p className="text-[10px] text-[#6B8A8F] uppercase font-bold tracking-wider">Locations</p>
            </div>
            <div className="bg-white border border-[#0C2E3C]/10 p-4 text-center">
              <Trophy size={20} className="mx-auto text-[#C8E64E] mb-1" />
              <p className="text-2xl font-bold text-[#0C2E3C]">{mapData.area_density?.length || 0}</p>
              <p className="text-[10px] text-[#6B8A8F] uppercase font-bold tracking-wider">Communities</p>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0C2E3C]" /></div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div className="lg:col-span-3">
              <div className="bg-white border border-[#0C2E3C]/10 overflow-hidden rounded-sm" style={{ height: "500px" }} data-testid="team-map">
                <MapContainer center={DEFAULT_CENTER} zoom={DEFAULT_ZOOM} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
                  <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                  <FitBounds locations={validLocations} />
                  {validLocations.map((loc) => (
                    <Marker key={loc.location_id} position={[loc.lat, loc.lng]} icon={makeMarkerIcon(loc.marker_color || "#0C2E3C")}>
                      <Popup>
                        <div className="text-center min-w-[140px]">
                          <strong style={{ color: loc.marker_color || "#0C2E3C" }}>{loc.name}</strong>
                          <br /><span className="text-xs text-gray-500 capitalize">{loc.location_type}</span>
                          {loc.address && <><br /><span className="text-xs">{loc.address}</span></>}
                          {loc.description && <><br /><span className="text-xs text-gray-600">{loc.description}</span></>}
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              </div>
              {/* Legend */}
              {validLocations.length > 0 && (
                <div className="flex items-center gap-4 mt-3 flex-wrap" data-testid="map-legend">
                  {validLocations.map((loc) => (
                    <span key={loc.location_id} className="flex items-center gap-1.5 text-[11px] text-[#6B8A8F]">
                      <span className="w-3.5 h-3.5 rounded-full border-2 border-white shadow-sm" style={{ backgroundColor: loc.marker_color || "#0C2E3C" }} />
                      {loc.name}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div>
              <h3 className="font-heading font-bold text-[#0C2E3C] text-sm uppercase tracking-wider mb-3">Locations</h3>
              {validLocations.length === 0 ? (
                <p className="text-xs text-[#6B8A8F]">Locations coming soon</p>
              ) : (
                <div className="space-y-2 mb-6">
                  {validLocations.map((loc) => (
                    <div key={loc.location_id} className="bg-white border border-[#0C2E3C]/10 p-3" data-testid={`location-card-${loc.location_id}`}>
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="w-3.5 h-3.5 rounded-full border border-white shadow-sm" style={{ backgroundColor: loc.marker_color || "#0C2E3C" }} />
                        <h4 className="font-bold text-sm text-[#0C2E3C]">{loc.name}</h4>
                      </div>
                      <p className="text-[10px] text-[#6B8A8F] uppercase font-bold">{loc.location_type}</p>
                      {loc.address && <p className="text-xs text-[#6B8A8F] mt-0.5">{loc.address}</p>}
                    </div>
                  ))}
                </div>
              )}

              {mapData?.area_density?.length > 0 && (
                <>
                  <h3 className="font-heading font-bold text-[#0C2E3C] text-sm uppercase tracking-wider mb-3">Player Communities</h3>
                  <div className="space-y-1.5">
                    {mapData.area_density.map((a, i) => (
                      <div key={i} className="flex items-center justify-between bg-white border border-[#0C2E3C]/10 px-3 py-2">
                        <span className="text-sm text-[#0C2E3C] font-medium">{a.area}</span>
                        <span className="text-xs text-[#6B8A8F] font-bold">{a.count}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      <footer className="bg-[#0C2E3C] mt-16 py-8 text-center">
        <p className="text-[#F0F4F3]/50 text-xs">Commonwealth FC &middot; Charlottesville, VA</p>
      </footer>
    </div>
  );
};

export default TeamMapPage;
